import { useEffect, useState } from "react";
import {
  PUBLISHED_BUILD_RETRY_DELAYS_MS,
  isPublishedBuildReady,
  markAutoUpdateAttempted,
  reloadToLatestBuild,
  wasAutoUpdateAttempted,
} from "../appVersion";
import { isProtectedWorkActive, subscribeProtectedWork } from "../protectedWork";
import { useAppVersionGuard } from "./useAppVersionGuard";

export interface UseAppUpdateCoordinatorResult {
  /** Build obsoleta detectada, mas o reload foi adiado por trabalho protegido. */
  isUpdatePending: boolean;
}

/**
 * Coordena a atualização automática do app quando uma build nova é publicada,
 * para que o cliente migre sem depender de limpar cache, fechar a aba ou
 * recarregar manualmente (issue #49).
 *
 * Monte uma única vez no nível mais alto da aplicação: a checagem de versão
 * de `useAppVersionGuard` já revalida na montagem e quando a aba volta ao
 * primeiro plano. Quando a build fica obsoleta, o reload só acontece de
 * imediato se não houver trabalho protegido registrado (`useProtectedWork`);
 * caso contrário, a atualização fica pendente e é aplicada assim que o
 * trabalho protegido terminar, for cancelado ou voltar a um estado seguro.
 */
export function useAppUpdateCoordinator(): UseAppUpdateCoordinatorResult {
  const { isObsolete, deployedBuildId } = useAppVersionGuard();
  const [isUpdatePending, setIsUpdatePending] = useState(false);

  useEffect(() => {
    if (!isObsolete || !deployedBuildId) {
      setIsUpdatePending(false);
      return undefined;
    }
    if (wasAutoUpdateAttempted(deployedBuildId)) return undefined;

    let cancelled = false;
    let checking = false;
    let readinessFailures = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const attemptReload = async () => {
      if (cancelled || checking) return;
      if (isProtectedWorkActive()) {
        setIsUpdatePending(true);
        return;
      }
      checking = true;
      const ready = await isPublishedBuildReady();
      checking = false;
      if (cancelled) return;
      // O operador pode ter começado um formulário durante a verificação.
      if (isProtectedWorkActive()) {
        setIsUpdatePending(true);
        return;
      }
      if (!ready) {
        // Publicação incompleta: não marca a tentativa e verifica de novo
        // algumas vezes, sem polling contínuo.
        clearTimeout(retryTimer);
        const delay = PUBLISHED_BUILD_RETRY_DELAYS_MS[readinessFailures];
        readinessFailures += 1;
        if (delay !== undefined) {
          retryTimer = setTimeout(() => void attemptReload(), delay);
        }
        return;
      }
      setIsUpdatePending(false);
      markAutoUpdateAttempted(deployedBuildId);
      reloadToLatestBuild();
    };

    void attemptReload();
    const unsubscribe = subscribeProtectedWork((active) => {
      if (!active) void attemptReload();
    });
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      unsubscribe();
    };
  }, [isObsolete, deployedBuildId]);

  return { isUpdatePending };
}
